import React from 'react';
import { Logo } from './Logo';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <header className="border-b border-gray-800">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="cursor-pointer" onClick={() => navigate('/')}>
            <Logo height={40} />
          </div>
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-gray-400 hover:text-[#D4F7DC] transition-colors"
          >
            <ArrowLeft size={18} />
            Back to Home
          </button>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-4xl font-bold mb-2">Privacy Policy</h1>
        <p className="text-gray-400 mb-8">HiBotics AI</p>
        <p className="text-sm text-gray-500 mb-12">Last updated: 30 January 2026</p>

        <div className="prose prose-invert max-w-none">
          <p className="text-gray-300 leading-relaxed mb-8">
            HiBotics AI ("we", "us", "our") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, store, and protect personal data when you visit our website or use our services.
          </p>
          <p className="text-gray-300 leading-relaxed mb-12">
            This policy complies with the UK General Data Protection Regulation (UK GDPR) and the Data Protection Act 2018.
          </p>

          {/* Section 1 */}
          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-[#D4F7DC] mb-4">1. Who We Are</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              HiBotics AI provides AI-powered voice and automation solutions for small businesses in the United Kingdom.
            </p>
            <ul className="text-gray-300 space-y-2 ml-4">
              <li><strong className="text-white">Business name:</strong> HiBotics AI</li>
              <li><strong className="text-white">Website:</strong> <a href="https://hiboticsai.com" className="text-[#D4F7DC] hover:underline">https://hiboticsai.com</a></li>
              <li><strong className="text-white">Contact email:</strong> <a href="mailto:info@hiboticsai.com" className="text-[#D4F7DC] hover:underline">info@hiboticsai.com</a></li>
            </ul>
          </section>

          {/* Section 2 */}
          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-[#D4F7DC] mb-4">2. Information We Collect</h2>
            <p className="text-gray-300 leading-relaxed mb-4">We may collect the following information:</p>
            
            <h3 className="text-lg font-medium text-white mt-6 mb-3">a) Information you provide directly</h3>
            <ul className="text-gray-300 space-y-1 ml-4 list-disc list-inside">
              <li>Name</li>
              <li>Business name</li>
              <li>Email address</li>
              <li>Phone number</li>
              <li>Enquiry details submitted via contact forms</li>
            </ul>

            <h3 className="text-lg font-medium text-white mt-6 mb-3">b) Information collected automatically</h3>
            <p className="text-gray-300 mb-2">When you visit our website, we may collect:</p>
            <ul className="text-gray-300 space-y-1 ml-4 list-disc list-inside">
              <li>IP address</li>
              <li>Browser type</li>
              <li>Device information</li>
              <li>Pages visited</li>
              <li>Date and time of visit</li>
            </ul>
            <p className="text-gray-400 text-sm mt-2 italic">This data is collected using cookies and analytics tools.</p>

            <h3 className="text-lg font-medium text-white mt-6 mb-3">c) Voice & booking data (for clients)</h3>
            <p className="text-gray-300 mb-2">When using our AI receptionist services, we may process:</p>
            <ul className="text-gray-300 space-y-1 ml-4 list-disc list-inside">
              <li>Caller phone numbers</li>
              <li>Call recordings or transcripts</li>
              <li>Booking details (date, time, service type)</li>
              <li>Calendar availability information</li>
            </ul>
            <p className="text-gray-400 text-sm mt-2 italic">This data is processed solely to deliver the service.</p>
          </section>

          {/* Section 3 */}
          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-[#D4F7DC] mb-4">3. How We Use Your Information</h2>
            <p className="text-gray-300 leading-relaxed mb-4">We use personal data to:</p>
            <ul className="text-gray-300 space-y-1 ml-4 list-disc list-inside">
              <li>Respond to enquiries</li>
              <li>Provide and maintain our AI services</li>
              <li>Manage bookings and calendars</li>
              <li>Improve system performance</li>
              <li>Provide customer support</li>
              <li>Meet legal and regulatory obligations</li>
            </ul>
            <p className="text-white font-medium mt-4">We do not sell personal data to third parties.</p>
          </section>

          {/* Section 4 */}
          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-[#D4F7DC] mb-4">4. Legal Basis for Processing</h2>
            <p className="text-gray-300 leading-relaxed mb-4">Under UK GDPR, we process data based on:</p>
            <ul className="text-gray-300 space-y-2 ml-4">
              <li><strong className="text-white">Consent</strong> – when you contact us or submit forms</li>
              <li><strong className="text-white">Contract</strong> – when delivering agreed services</li>
              <li><strong className="text-white">Legitimate interest</strong> – improving and operating our services</li>
              <li><strong className="text-white">Legal obligation</strong> – where required by law</li>
            </ul>
          </section>

          {/* Section 5 */}
          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-[#D4F7DC] mb-4">5. Data Sharing</h2>
            <p className="text-gray-300 leading-relaxed mb-4">We may share limited data with trusted third-party providers, including:</p>
            <ul className="text-gray-300 space-y-1 ml-4 list-disc list-inside">
              <li>AI voice and telephony platforms</li>
              <li>Calendar and scheduling systems</li>
              <li>Hosting and cloud infrastructure providers</li>
            </ul>
            <p className="text-gray-400 text-sm mt-2 italic">These providers process data only on our instructions and in accordance with GDPR.</p>
          </section>

          {/* Section 6 */}
          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-[#D4F7DC] mb-4">6. Data Storage & Security</h2>
            <p className="text-gray-300 leading-relaxed mb-4">We take reasonable steps to protect your data, including:</p>
            <ul className="text-gray-300 space-y-1 ml-4 list-disc list-inside">
              <li>Secure cloud infrastructure</li>
              <li>Restricted access to systems</li>
              <li>Encrypted connections where available</li>
            </ul>
            <p className="text-gray-300 mt-4">Data is retained only for as long as necessary to provide services or meet legal requirements.</p>
          </section>

          {/* Section 7 */}
          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-[#D4F7DC] mb-4">7. Cookies</h2>
            <p className="text-gray-300 leading-relaxed mb-4">Our website uses cookies to:</p>
            <ul className="text-gray-300 space-y-1 ml-4 list-disc list-inside">
              <li>Improve user experience</li>
              <li>Analyse website traffic</li>
              <li>Understand how visitors use our site</li>
            </ul>
            <p className="text-gray-300 mt-4">You can control cookies through your browser settings.</p>
          </section>

          {/* Section 8 */}
          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-[#D4F7DC] mb-4">8. Your Data Protection Rights</h2>
            <p className="text-gray-300 leading-relaxed mb-4">Under UK GDPR, you have the right to:</p>
            <ul className="text-gray-300 space-y-1 ml-4 list-disc list-inside">
              <li>Access your personal data</li>
              <li>Request correction of inaccurate data</li>
              <li>Request deletion of your data</li>
              <li>Object to processing</li>
              <li>Request data portability</li>
              <li>Withdraw consent at any time</li>
            </ul>
            <p className="text-gray-300 mt-4">
              To exercise your rights, contact us at: <a href="mailto:info@hiboticsai.com" className="text-[#D4F7DC] hover:underline">info@hiboticsai.com</a>
            </p>
          </section>

          {/* Section 9 */}
          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-[#D4F7DC] mb-4">9. Third-Party Links</h2>
            <p className="text-gray-300 leading-relaxed">
              Our website may contain links to third-party websites. We are not responsible for the privacy practices of those sites.
            </p>
          </section>

          {/* Section 10 */}
          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-[#D4F7DC] mb-4">10. Changes to This Policy</h2>
            <p className="text-gray-300 leading-relaxed">
              We may update this Privacy Policy from time to time. The latest version will always be available on our website.
            </p>
          </section>

          {/* Section 11 */}
          <section className="mb-10">
            <h2 className="text-2xl font-semibold text-[#D4F7DC] mb-4">11. Contact Us</h2>
            <p className="text-gray-300 leading-relaxed mb-4">
              If you have any questions about this Privacy Policy or how your data is handled, please contact:
            </p>
            <div className="bg-gray-900 border border-gray-800 rounded-lg p-6">
              <p className="text-white font-semibold mb-2">HiBotics AI</p>
              <p className="text-gray-300">
                Email: <a href="mailto:info@hiboticsai.com" className="text-[#D4F7DC] hover:underline">info@hiboticsai.com</a>
              </p>
              <p className="text-gray-300">
                Website: <a href="https://hiboticsai.com" className="text-[#D4F7DC] hover:underline">https://hiboticsai.com</a>
              </p>
            </div>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-gray-800 py-6">
        <div className="max-w-4xl mx-auto px-6 text-center text-gray-500 text-sm">
          © {new Date().getFullYear()} HiBotics AI. All rights reserved.
        </div>
      </footer>
    </div>
  );
};
